'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  getMafiaAudioEngine,
  type MafiaAudioEngine,
  type MafiaSfx,
} from './audio-engine'

const STORAGE_KEY = 'mafia-audio-prefs'

interface AudioPrefs {
  muted: boolean
  music: number
  sfx: number
  enabled: boolean
}

function loadPrefs(): AudioPrefs {
  if (typeof window === 'undefined') {
    return { muted: false, music: 0.35, sfx: 0.7, enabled: true }
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { muted: false, music: 0.35, sfx: 0.7, enabled: true }
    return { muted: false, music: 0.35, sfx: 0.7, enabled: true, ...JSON.parse(raw) }
  } catch {
    return { muted: false, music: 0.35, sfx: 0.7, enabled: true }
  }
}

function savePrefs(prefs: AudioPrefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs))
  } catch {
    /* ignore */
  }
}

export function useMafiaAudio(options?: {
  phase?: string
  speaking?: boolean
  lastDeathId?: string
}) {
  const engineRef = useRef<MafiaAudioEngine | null>(null)
  const [prefs, setPrefs] = useState<AudioPrefs>(loadPrefs)
  const [ready, setReady] = useState(false)
  const prevPhase = useRef<string | undefined>(undefined)
  const prevDeath = useRef<string | undefined>(undefined)

  useEffect(() => {
    engineRef.current = getMafiaAudioEngine()
    const eng = engineRef.current
    eng.setMuted(prefs.muted)
    eng.setMusicVolume(prefs.music)
    eng.setSfxVolume(prefs.sfx)
    return () => {
      /* keep shared engine across navigations */
    }
  }, [prefs.muted, prefs.music, prefs.sfx])

  const enable = useCallback(async () => {
    const eng = getMafiaAudioEngine()
    engineRef.current = eng
    await eng.unlock()
    eng.setMuted(prefs.muted)
    eng.setMusicVolume(prefs.music)
    eng.setSfxVolume(prefs.sfx)
    if (prefs.enabled && !prefs.muted) {
      await eng.startAmbient()
    }
    setReady(true)
    const next = { ...prefs, enabled: true }
    setPrefs(next)
    savePrefs(next)
  }, [prefs])

  const play = useCallback(
    async (sfx: MafiaSfx) => {
      if (!prefs.enabled || prefs.muted) return
      await engineRef.current?.playSfx(sfx)
    },
    [prefs.enabled, prefs.muted]
  )

  const updatePrefs = useCallback((patch: Partial<AudioPrefs>) => {
    setPrefs((prev) => {
      const next = { ...prev, ...patch }
      savePrefs(next)
      const eng = getMafiaAudioEngine()
      eng.setMuted(next.muted)
      eng.setMusicVolume(next.music)
      eng.setSfxVolume(next.sfx)
      if (next.muted || !next.enabled) {
        void eng.stopAmbient()
      } else if (eng.isUnlocked()) {
        void eng.startAmbient()
      }
      return next
    })
  }, [])

  // Duck under speech
  useEffect(() => {
    engineRef.current?.setDucking(Boolean(options?.speaking))
  }, [options?.speaking])

  // Phase / death cues
  useEffect(() => {
    const phase = options?.phase
    if (!phase || !ready || prefs.muted) {
      prevPhase.current = phase
      return
    }
    if (prevPhase.current && prevPhase.current !== phase) {
      const night = phase.startsWith('night')
      void play(night ? 'night' : 'phase')
    }
    prevPhase.current = phase
  }, [options?.phase, ready, prefs.muted, play])

  useEffect(() => {
    const deathId = options?.lastDeathId
    if (!deathId || !ready || prefs.muted) {
      prevDeath.current = deathId
      return
    }
    if (prevDeath.current !== deathId) {
      void play('death')
    }
    prevDeath.current = deathId
  }, [options?.lastDeathId, ready, prefs.muted, play])

  return {
    ready,
    prefs,
    enable,
    play,
    updatePrefs,
  }
}
